/**
 * jr-privacy-request.js — the privacy request page (templates/page.privacy-request.liquid).
 *
 * Two screens over the public `privacy-request` edge function:
 *   SUBMIT   what do you want (five choices), your email, optional name / details /
 *            state / authorized-agent flag. The reply is the same for everyone.
 *   CONFIRM  reached from the emailed link (?confirm=<token> or #confirm=<token>). The
 *            page first PEEKS (peek:true) so it can show what the request will do —
 *            a deletion carries the irreversibility warning — and confirms only on the
 *            button. The token is read once and taken out of the address bar.
 *
 * Nothing here looks anything up. All text is set with textContent / createElement;
 * nothing from the network is ever written as HTML.
 *
 * Pure pieces (buildSubmission, readToken, confirmCopy) are exposed on
 * window.jrPrivacyRequest for tools/jr-privacy-request.test.mjs.
 */
(function () {
  'use strict';

  var KINDS = [
    { id: 'delete',          label: 'Delete my personal information',
      hint: 'We erase what we hold about you, and ask the services we use to do the same. Some records must be kept by law (see below).' },
    { id: 'access',          label: 'Send me a copy of my personal information',
      hint: 'What we hold, where it came from, and who we have shared it with.' },
    { id: 'correct',         label: 'Correct my personal information',
      hint: 'Tell us what is wrong in the details box.' },
    { id: 'opt_out_sale',    label: 'Opt out of the sale or sharing of my personal information',
      hint: 'This stops our advertising partners receiving your data. You can also do this any time from "Your Privacy Choices" in the footer.' },
    { id: 'limit_sensitive', label: 'Limit the use of my sensitive personal information',
      hint: 'We collect very little that qualifies, but the right is yours to exercise.' }
  ];

  var CONFIRM = {
    delete: {
      title: 'Confirm: delete my personal information',
      body: 'Once confirmed, this cannot be undone. Your account, order history as it relates to you, support conversations and marketing profile will be erased, and any active subscription will be cancelled first so nothing is billed afterwards. A few records are kept because the law requires it — order totals for tax and accounting, with your name and address removed — and backups age out on their own cycle.',
      button: 'Yes, delete my information'
    },
    access: { title: 'Confirm: send me a copy of my information', body: 'We will send a copy of what we hold to the address on this request within 45 days.', button: 'Confirm my request' },
    correct: { title: 'Confirm: correct my information', body: 'We will review the correction you asked for and reply to the address on this request within 45 days.', button: 'Confirm my request' },
    opt_out_sale: { title: 'Confirm: opt out of sale or sharing', body: 'Your data will not be sold or shared with our advertising partners. This takes effect right away and is honoured even if you close this page.', button: 'Confirm my opt-out' },
    limit_sensitive: { title: 'Confirm: limit use of my sensitive information', body: 'We will limit the use of any sensitive personal information we hold to what is needed to provide what you bought.', button: 'Confirm my request' }
  };

  // ── pure helpers (also used by the tests) ───────────────────────────────────
  function buildSubmission(f) {
    var email = String(f.email || '').trim();
    var kind = String(f.kind || '');
    var errors = [];
    if (!KINDS.some(function (k) { return k.id === kind; })) errors.push('Please choose what you are asking for.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push('Please enter the email address you use with us.');
    var region = String(f.region || '').trim().toUpperCase();
    var body = {
      brand: 'jr', action: 'submit', kind: kind, email: email,
      name: String(f.name || '').trim().slice(0, 120) || undefined,
      message: String(f.message || '').trim().slice(0, 4000) || undefined,
      region: /^[A-Z]{2}(-?[A-Z0-9]{1,3})?$/.test(region) ? region : undefined,
      is_authorized_agent: f.is_authorized_agent === true,
      company: String(f.company || '') || undefined       // honeypot: a person never fills it
    };
    return { ok: errors.length === 0, errors: errors, body: body };
  }

  /** The emailed link carries the token as ?confirm= or #confirm=. Both are accepted. */
  function readToken(search, hash) {
    var q = new URLSearchParams(search || '').get('confirm') || new URLSearchParams((hash || '').replace(/^#/, '')).get('confirm') || '';
    return /^[0-9a-f]{64}$/.test(q) ? q : null;
  }

  function confirmCopy(kind) { return CONFIRM[kind] || CONFIRM.access; }

  window.jrPrivacyRequest = { buildSubmission: buildSubmission, readToken: readToken, confirmCopy: confirmCopy, KINDS: KINDS };

  var host = document.querySelector('.jr-privacy');
  var root = document.getElementById('jr-privacy-root');
  if (!host || !root) return;
  var ENDPOINT = host.getAttribute('data-endpoint');
  var CONTACT = host.getAttribute('data-contact-email') || 'support@jeremysrazors.com';

  // ── tiny DOM helpers ──────────────────────────────────────────────────────────
  function el(tag, cls, text) { var e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }
  function post(body) {
    return fetch(ENDPOINT, { method: 'POST', headers: { 'content-type': 'text/plain;charset=UTF-8' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { status: r.status, json: j }; }); });
  }
  function head(eyebrow, title, intro) {
    var h = el('div', 'jr-survey__head');
    h.appendChild(el('p', 'jr-survey__eyebrow', eyebrow));
    h.appendChild(el('h1', 'jr-survey__title', title));
    if (intro) h.appendChild(el('p', 'jr-survey__intro', intro));
    return h;
  }
  function contactLine() {
    var p = el('p', 'jr-survey__note');
    p.appendChild(document.createTextNode('Prefer email? Write to '));
    var a = el('a', null, CONTACT); a.href = 'mailto:' + CONTACT + '?subject=' + encodeURIComponent('Privacy request');
    p.appendChild(a);
    p.appendChild(document.createTextNode(' with "privacy request" in the subject. Either way we acknowledge within 10 business days and respond within 45 days.'));
    return p;
  }

  // ── screen 1: the form ────────────────────────────────────────────────────────
  function renderForm() {
    clear(root);
    root.appendChild(head("Jeremy's Razors", 'Your privacy requests',
      'Ask us to delete, send, or correct the personal information we hold about you, or to stop selling or sharing it. We will email you a link to confirm the request; nothing happens until you open it.'));

    var form = el('form', 'jr-privacy__form'); form.noValidate = true; form.setAttribute('autocomplete', 'on');

    var fs = el('fieldset', 'jr-q');
    fs.appendChild(el('legend', 'jr-q__label', 'What would you like us to do?'));
    var opts = el('div', 'jr-opts');
    KINDS.forEach(function (k, i) {
      var lab = el('label', 'jr-opt');
      var inp = el('input'); inp.type = 'radio'; inp.name = 'kind'; inp.value = k.id; inp.id = 'kind-' + k.id;
      var span = el('span'); span.textContent = k.label;
      lab.appendChild(inp); lab.appendChild(span); opts.appendChild(lab);
      var hint = el('p', 'jr-privacy__hint', k.hint); hint.id = 'hint-' + k.id; opts.appendChild(hint);
      inp.addEventListener('change', function () {
        Array.prototype.forEach.call(opts.querySelectorAll('.jr-privacy__hint'), function (h) { h.classList.remove('is-on'); });
        hint.classList.add('is-on');
      });
    });
    fs.appendChild(opts); form.appendChild(fs);

    function field(id, label, type, hint, attrs) {
      var q = el('div', 'jr-q');
      var l = el('label', 'jr-q__label', label); l.htmlFor = id; q.appendChild(l);
      if (hint) q.appendChild(el('p', 'jr-q__hint', hint));
      var input = el(type === 'textarea' ? 'textarea' : 'input', type === 'textarea' ? 'jr-textarea' : 'jr-input');
      input.id = id; input.name = id; if (type !== 'textarea') input.type = type;
      Object.keys(attrs || {}).forEach(function (k) { input.setAttribute(k, attrs[k]); });
      q.appendChild(input); return q;
    }
    form.appendChild(field('email', 'Your email address', 'email', 'The one you use with us. The confirmation link goes here.', { autocomplete: 'email', inputmode: 'email', required: 'required' }));
    form.appendChild(field('name', 'Your name (optional)', 'text', null, { autocomplete: 'name', maxlength: '120' }));
    form.appendChild(field('message', 'Anything that helps us find your records (optional)', 'textarea', 'An order number, another email you have used with us, or what needs correcting.', { maxlength: '4000' }));
    form.appendChild(field('region', 'State (optional)', 'text', 'Two letters, e.g. CA. Some rights depend on where you live.', { maxlength: '6', autocomplete: 'address-level1' }));

    var agent = el('label', 'jr-opt jr-privacy__agent');
    var ag = el('input'); ag.type = 'checkbox'; ag.name = 'is_authorized_agent'; ag.id = 'is_authorized_agent';
    agent.appendChild(ag); agent.appendChild(el('span', null, 'I am an authorized agent submitting this on someone else’s behalf'));
    form.appendChild(agent);

    // honeypot — off-screen, tabindex -1, never filled by a person
    var hp = el('div', 'jr-privacy__hp'); var hpi = el('input'); hpi.type = 'text'; hpi.name = 'company'; hpi.id = 'company'; hpi.tabIndex = -1; hpi.setAttribute('autocomplete', 'off'); hpi.setAttribute('aria-hidden', 'true');
    hp.appendChild(hpi); form.appendChild(hp);

    var foot = el('div', 'jr-survey__foot');
    var msg = el('p', 'jr-survey__msg'); msg.id = 'jr-privacy-msg'; foot.appendChild(msg);
    var btn = el('button', 'jr-survey__btn', 'Send my request'); btn.type = 'submit'; foot.appendChild(btn);
    foot.appendChild(contactLine());
    var choices = el('p', 'jr-survey__note'); choices.appendChild(document.createTextNode('To change cookie and advertising choices without a request, use '));
    var ca = el('a', null, 'Your Privacy Choices'); ca.href = '#privacy-choices'; choices.appendChild(ca); choices.appendChild(document.createTextNode('.'));
    foot.appendChild(choices);
    form.appendChild(foot);

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var f = {
        kind: (form.querySelector('input[name=kind]:checked') || {}).value,
        email: form.email.value, name: form.name.value, message: form.message.value, region: form.region.value,
        is_authorized_agent: ag.checked, company: hpi.value
      };
      var s = buildSubmission(f);
      if (!s.ok) { msg.textContent = s.errors[0]; msg.classList.add('is-error'); form.querySelector(s.errors[0].indexOf('choose') > -1 ? 'input[name=kind]' : '#email').focus(); return; }
      msg.textContent = ''; btn.disabled = true; btn.textContent = 'Sending…';
      post(s.body).then(function (r) {
        if (r.status === 200 && r.json && r.json.ok) { renderSent(r.json.message, s.body.kind); return; }
        msg.textContent = (r.json && r.json.error) || 'We could not send your request. Please try again in a moment, or email us.';
        msg.classList.add('is-error'); btn.disabled = false; btn.textContent = 'Send my request';
      }).catch(function () {
        msg.textContent = 'We could not reach our server. Please try again, or email us.'; msg.classList.add('is-error');
        btn.disabled = false; btn.textContent = 'Send my request';
      });
    });
    root.appendChild(form);
  }

  function renderSent(message, kind) {
    clear(root);
    var d = el('div', 'jr-survey__done');
    d.appendChild(head("Jeremy's Razors", 'Check your email', message || 'If that address can receive mail, a confirmation link is on its way. Your request is not confirmed until you open it.'));
    var p = el('p', 'jr-privacy__p', 'The link works once and expires in 48 hours. If it does not arrive, check your spam folder, then submit again.');
    d.appendChild(p);
    if (kind === 'opt_out_sale') d.appendChild(el('p', 'jr-privacy__p', 'Your opt-out of sale or sharing is honoured from now, whether or not you open the link.'));
    d.appendChild(contactLine());
    root.appendChild(d);
  }

  // ── screen 2: confirm from the emailed link ───────────────────────────────────
  function renderConfirm(token) {
    clear(root);
    root.appendChild(head("Jeremy's Razors", 'Confirming your request', 'One moment…'));
    post({ brand: 'jr', action: 'verify', token: token, peek: true }).then(function (r) {
      clear(root);
      if (!(r.status === 200 && r.json && r.json.ok)) { renderProblem((r.json && r.json.error) || 'That link is not valid.'); return; }
      var copy = confirmCopy(r.json.kind);
      root.appendChild(head("Jeremy's Razors", copy.title, copy.body));
      var foot = el('div', 'jr-survey__foot');
      var msg = el('p', 'jr-survey__msg'); foot.appendChild(msg);
      var btn = el('button', 'jr-survey__btn', copy.button); btn.type = 'button'; foot.appendChild(btn);
      var no = el('a', 'jr-survey__btn jr-survey__btn--quiet jr-privacy__cancel', 'Not now'); no.href = '/'; foot.appendChild(no);
      foot.appendChild(contactLine());
      root.appendChild(foot);
      btn.addEventListener('click', function () {
        btn.disabled = true; btn.textContent = 'Confirming…';
        post({ brand: 'jr', action: 'verify', token: token }).then(function (v) {
          if (v.status === 200 && v.json && v.json.verified) { renderDone(v.json.kind); return; }
          msg.textContent = (v.json && v.json.error) || 'We could not confirm the request. Please submit it again.'; msg.classList.add('is-error');
          btn.disabled = false; btn.textContent = copy.button;
        }).catch(function () { msg.textContent = 'We could not reach our server. Please try again.'; msg.classList.add('is-error'); btn.disabled = false; btn.textContent = copy.button; });
      });
    }).catch(function () { clear(root); renderProblem('We could not reach our server. Please try the link again in a moment.'); });
  }

  function renderDone(kind) {
    clear(root);
    var d = el('div', 'jr-survey__done');
    var lead = kind === 'delete'
      ? 'Your deletion request is confirmed. We will stop any active subscription first, then erase your information here and with the services we use, and email you when it is done — within 45 days.'
      : 'Your request is confirmed. We will respond to the address on the request within 45 days.';
    d.appendChild(head("Jeremy's Razors", 'Thank you', lead));
    d.appendChild(contactLine());
    var home = el('a', 'jr-survey__btn jr-survey__btn--quiet', 'Back to the store'); home.href = '/'; d.appendChild(home);
    root.appendChild(d);
  }

  function renderProblem(text) {
    var d = el('div', 'jr-survey__done');
    d.appendChild(head("Jeremy's Razors", 'That link did not work', text));
    var again = el('a', 'jr-survey__btn', 'Submit a new request'); again.href = window.location.pathname; d.appendChild(again);
    d.appendChild(contactLine());
    root.appendChild(d);
  }

  // ── boot: the token is read once and taken out of the address bar ──────────
  var token = readToken(window.location.search, window.location.hash);
  if (token) {
    try { window.history.replaceState(null, '', window.location.pathname); } catch (_) {}
    renderConfirm(token);
  } else {
    renderForm();
  }
})();
